import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * The signed-in employee's own on-call records — assignments, swap requests and
 * compensations. Mirrors datacentral-client-v4 `modules/Oncall/services/*` "my"
 * endpoints, consumed by the Account Home "On-call" tab. All take a date window
 * (`start_date` / `end_date`, "YYYY-MM-DD").
 */

export type AssignmentStatus = 'scheduled' | 'active' | 'completed' | 'cancelled';
export type SwapStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type CompensationStatus = 'pending' | 'approved' | 'rejected' | 'paid';

export interface MyOncallAssignment {
  oncall_assignment_id: number;
  oncall_schedule_id: number;
  oncall_schedule_name: string;
  department_name?: string;
  /** "YYYY-MM-DD" */
  oncall_date: string;
  /** ISO datetime, e.g. "2025-01-15T08:00:00Z" */
  oncall_start_time: string;
  oncall_end_time: string;
  oncall_assignment_status: AssignmentStatus;
}

export interface MyOncallSwap {
  oncall_swap_id: number;
  oncall_assignment_id: number;
  oncall_schedule_name: string;
  oncall_date: string;
  requester_employee_id: number;
  requester_name: string;
  target_employee_id: number;
  target_name: string;
  oncall_swap_status: SwapStatus;
}

export interface MyOncallCompensation {
  oncall_compensation_id: number;
  oncall_schedule_name: string;
  oncall_date: string;
  oncall_hours: number;
  incident_count: number;
  /** 1.0 / 1.5 / 2.0 … */
  compensation_rate: number;
  compensation_amount: number | null;
  oncall_compensation_status: CompensationStatus;
  approved_by_name: string | null;
  approved_at: string | null;
  oncall_compensation_note: string | null;
}

function list<T>(url: string): Promise<T[]> {
  return dataCentralApi.get<ResponseArray<T>>(url).then((res) => {
    const data = res.data as ResponseArray<T> & { result?: T[] };
    return data.results ?? data.result ?? [];
  });
}

const win = (start: string, end: string) => `start_date=${start}&end_date=${end}`;

export function useMyOncallAssignments(start: string, end: string) {
  return useQuery({
    queryKey: ['account', 'oncall', 'assignments', start, end],
    queryFn: () => list<MyOncallAssignment>(`/oncall/assignments/my?${win(start, end)}`),
  });
}

export function useMyOncallSwaps(start: string, end: string) {
  return useQuery({
    queryKey: ['account', 'oncall', 'swaps', start, end],
    queryFn: () => list<MyOncallSwap>(`/oncall/swaps/my?${win(start, end)}`),
  });
}

export function useMyOncallCompensations(start: string, end: string) {
  return useQuery({
    queryKey: ['account', 'oncall', 'compensations', start, end],
    queryFn: () => list<MyOncallCompensation>(`/oncall/compensations/my?${win(start, end)}`),
  });
}
